import { ChakraProvider } from '@chakra-ui/react';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingSpinner from 'components/LoadingSpinner';
import PrivateRoute from 'components/PrivateRoute';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { HashRouter, Redirect, Route, Switch } from 'react-router-dom';
import './utils/assets/css/App.css';
import keycloak from './utils/keycloak';
import AdminLayout from './utils/layouts';
import theme from './utils/theme/theme';
import { hasRole } from './utils/auth-utils';
import LoginPage from 'pages/auth/LoginPage';
import { LoginTokens } from 'services/AuthService';

const clearStoredTokens = () => {
  localStorage.removeItem('kc-token');
  localStorage.removeItem('kc-refresh-token');
  localStorage.removeItem('kc-id-token');
};

const RoleBasedRedirect = () => {
  const isAdmin = hasRole(keycloak, 'admin') || hasRole(keycloak, 'ADMIN');
  return <Redirect from="/" to={isAdmin ? '/admin/home' : '/dipendente/interventi'} />;
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  // Computed once per page load: if a previous custom-form login left tokens behind, hand them
  // straight to keycloak-js so it validates/refreshes them with no redirect at all (same as it
  // already does today for a returning authenticated session). Otherwise fall back to the
  // existing silent SSO check (a hidden iframe, never a visible top-level redirect) so a user
  // who's already got a live Keycloak session elsewhere still skips the login form.
  const [initOptions] = useState(() => {
    const storedToken = localStorage.getItem('kc-token');
    const storedRefreshToken = localStorage.getItem('kc-refresh-token');
    if (storedToken && storedRefreshToken) {
      return {
        onLoad: undefined,
        token: storedToken,
        refreshToken: storedRefreshToken,
        idToken: localStorage.getItem('kc-id-token') || undefined,
        // keycloak-js's session-iframe monitor compares against Keycloak's own browser-side session
        // cookie — but a Direct Grant login never navigates the browser to Keycloak's domain, so
        // that cookie is never set. Left enabled, the iframe check reports "no session" almost
        // immediately and fires a false onAuthLogout even though the tokens are perfectly valid.
        checkLoginIframe: false,
      };
    }
    return { onLoad: 'check-sso' as const, checkLoginIframe: false };
  });

  const handleLoginSuccess = (tokens: LoginTokens) => {
    localStorage.setItem('kc-token', tokens.accessToken);
    localStorage.setItem('kc-refresh-token', tokens.refreshToken);
    if (tokens.idToken) localStorage.setItem('kc-id-token', tokens.idToken);
    // Full reload rather than re-initializing keycloak-js in place: it lets ReactKeycloakProvider
    // bootstrap fresh with initOptions picking up the tokens we just stored, the exact same code
    // path already used (and already proven) for a returning authenticated session.
    window.location.reload();
  };

  // Any failure that leaves us unauthenticated (no session, a rejected/expired stored refresh
  // token, a failed background refresh, a logout) all have the exact same correct recovery: show
  // the login form. There's no real "retry the same thing" step worth interposing — the previous
  // generic error screen with a "Riprova" button just reloaded the page, which only worked because
  // the reload happened to hit a clean init the second time around. Going to the login form
  // directly removes that redundant extra click.
  const goToLogin = () => {
    clearStoredTokens();
    setShowLogin(true);
    setIsLoading(false);
  };

  useEffect(() => {
    keycloak.onReady = () => {
      if (!keycloak.authenticated) {
        goToLogin();
      } else {
        setIsLoading(false);
      }
    };
    keycloak.onAuthError = () => goToLogin();
    keycloak.onAuthLogout = () => goToLogin();
    keycloak.onAuthRefreshError = () => goToLogin();
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <React.StrictMode>
        <ReactKeycloakProvider
          authClient={keycloak}
          initOptions={initOptions}
          onEvent={(event) => {
            if (event === 'onAuthSuccess') {
              if (keycloak.token) {
                localStorage.setItem('kc-token', keycloak.token);
              }
            } else if (event === 'onInitError') {
              // Fires when keycloak.init() itself rejects — notably when a stored token/refreshToken
              // handed in via initOptions is rejected right away (e.g. "Token is not active" on an
              // expired refresh token). This happens before onReady/onAuthError ever get a chance to
              // fire, so it's the only place that can catch this specific case.
              goToLogin();
            }
          }}
          onTokens={(tokens) => {
            if (tokens.token) {
              localStorage.setItem('kc-token', tokens.token);
            }
          }}
        >
          {isLoading ? (
            <LoadingSpinner />
          ) : showLogin ? (
            <LoginPage onLoginSuccess={handleLoginSuccess} />
          ) : (
            <HashRouter>
              <Switch>
                <PrivateRoute path="/admin" component={AdminLayout} />
                <PrivateRoute path="/dipendente" component={AdminLayout} />
                <Route path="/" component={RoleBasedRedirect} />
              </Switch>
            </HashRouter>
          )}
        </ReactKeycloakProvider>
      </React.StrictMode>
      <ToastContainer position="bottom-right" />
    </ChakraProvider>
  );
};

ReactDOM.render(<App />, document.getElementById('root'));
